/**
 * Copyright 2025 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */

// This file was ported from WebSDK/src/sdk/audio/AudioVolumeMeter.js

export default class AudioVolumeMeter {
  static defaultAlpha = 1 / 16;

  constructor() {
    this._context = null;
    this._alpha = AudioVolumeMeter.defaultAlpha;
    this._value = 0.0;
    this._smoothedValue = 0.0;
    this._smoothedPeakValue = 0.0;
    this._clipped = 0;
    this._scriptProcessor = null;
    this._callback = null;
    this._mediaStreamSource = null;
  }

  init(context, alpha = AudioVolumeMeter.defaultAlpha) {
    alpha = parseFloat(alpha);

    this._context = context;
    this._alpha = alpha;
    this._value = 0.0;
    this._smoothedValue = 0.0;
    this._smoothedPeakValue = 0.0;
    this._clipped = 0;

    this._scriptProcessor = context.createScriptProcessor(4096, 1, 1);
    this._scriptProcessor.addEventListener('audioprocess', this._onAudioProcess.bind(this));
  }

  onValue(callback) {
    this._callback = callback;
  }

  getValue() {
    return this._value;
  }

  getSmoothedValue() {
    return this._smoothedValue;
  }

  getSmoothedPeakValue() {
    return this._smoothedPeakValue;
  }

  setAlpha(alpha) {
    this._alpha = parseFloat(alpha);
  }

  connect(stream) {
    this._connectToStream(stream);
  }

  stop() {
    this._stopConnections();
  }

  _onAudioProcess(event) {
    const input = event.inputBuffer.getChannelData(0);
    let sum = 0.0;
    let clipped = 0;

    for (let i = 0; i < input.length; i++) {
      const sample = input[i];
      sum += sample * sample;

      if (Math.abs(sample) > 0.99) {
        clipped++;
      }
    }

    this._value = Math.sqrt(sum / input.length);
    this._smoothedValue = this._alpha * this._value + (1.0 - this._alpha) * this._smoothedValue;
    this._smoothedPeakValue = Math.max(this._value, this._alpha * this._value + (1.0 - this._alpha) * this._smoothedPeakValue);
    this._clipped = clipped;

    if (this._callback) {
      this._callback.call(null, {
        date: Date.now(),
        value: this._value,
        smoothedValue: this._smoothedValue,
        smoothedPeakValue: this._smoothedPeakValue,
        clipped: this._clipped
      });
    }
  }

  _connectToStream(stream) {
    if (stream.getAudioTracks().length <= 0) {
      console.info('Stream has no audio tracks');

      return;
    }

    if (!this._context || !this._scriptProcessor) {
      console.error('AudioVolumeMeter not initialized before connecting.');

      return;
    }

    this._mediaStreamSource = this._context.createMediaStreamSource(stream);
    this._mediaStreamSource.connect(this._scriptProcessor);
    this._scriptProcessor.connect(this._context.destination);
  }

  _stopConnections() {
    if (this._mediaStreamSource) {
      this._mediaStreamSource.disconnect();
      this._mediaStreamSource = null;
    }

    if (this._scriptProcessor) {
      this._scriptProcessor.disconnect();
      this._scriptProcessor.removeEventListener('audioprocess', this._onAudioProcess.bind(this));
    }
  }
}